import { Progress, ProgressProps } from 'antd';
import { Link } from 'gatsby';
import React, { FC, useEffect, useState } from 'react';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import EmployeeThumb from '../../components/EmployeeThumb';
import SprintItem from '../../components/boxes/SprintItem';
import SprintListItem from '../../components/boxes/SprintListItem';
import LoadingSpinner from '../../components/buttons/LoadingSpinner';
import TextHeader from '../../components/headers/TextHeader';
import DownloadableThumbnail from '../../components/images/DownloadableThumbnail';
import LoadingSprints from '../../components/loading/LoadingSprints';
import Typography from '../../components/typography/Typography';
import { useCompany } from '../../hooks/useCompany';
import { SignedUrls, SqlAttachment } from '../../interfaces/Attachments';
import {
  PrjEmployeeInterface,
  PrjPhaseInterface,
  PrjProjectInterface,
} from '../../interfaces/Projects';
import { AttachmentService } from '../../services/attachments/AttachmentService';
import { ProjectsService } from '../../services/project/Projects';
import { TicketService } from '../../services/tickets/Tickets';
import { setRootAside } from '../../state/component/AsideSlice';
import { setCurrentProject } from '../../state/project/ProjectSlice';
import { RootState } from '../../state/store';
import ListComponent from '../../utils/ListComponent';
import ProjectUsers from './ProjectUsers';

type Props = {
  projectId: number;
};

const ProjectDetailContainer: FC<Props> = ({ projectId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [sprints, setSprints] = useState<PrjPhaseInterface[]>([]);
  const [attachments, setAttchments] = useState<SqlAttachment[]>([]);
  const [attachmentsLoading, setAttchmentsLoading] = useState<boolean>(false);
  const [showAttachments, setShowAttachments] = useState<boolean>(false);
  const [urls, setUrls] = useState<SignedUrls[]>([]);
  const [layout, setLayout] = useState<'Lijst' | 'Timeline'>('Lijst');
  const [filteredSprints, setFilteredSprints] = useState<PrjPhaseInterface[]>(
    []
  );
  const [contributors, setContributors] = useState<PrjEmployeeInterface[]>([]);
  const dispatch = useDispatch();
  const project = useSelector(
    (state: RootState) => state.project.currentProject
  ) as PrjProjectInterface | null;
  const selectedCompany = useSelector(
    (state: RootState) => state.companies.selectedCompany
  );

  const { companyId, companyName } = useCompany();

  const ticketService = new TicketService();
  const projectService = new ProjectsService();
  const attachmentService = new AttachmentService();
  const [totals, setTotals] = useState({
    phases: 0,
    phaseRules: 0,
    closedPhaseRules: 0,
  });

  if (!companyId) {
    return <div>Er ging iets mis.</div>;
  }

  const fetchProject = async () => {
    const project = await projectService.getProject(projectId, companyId);

    dispatch(setCurrentProject(project as PrjProjectInterface));
    setSprints(project?.phases);

    const totalPhaseRules =
      project?.phases.reduce((total, phase) => {
        return total + (phase.phase_rules?.length || 0);
      }, 0) ?? 0;

    const closedPhaseRules =
      project?.phases.reduce((total, phase) => {
        return (
          total +
          (phase.phase_rules?.filter(
            (rule) => rule.status.toLowerCase() === 'gesloten'
          ).length || 0)
        );
      }, 0) ?? 0;

    setTotals({
      phases: project?.phases.length,
      phaseRules: totalPhaseRules,
      closedPhaseRules: closedPhaseRules,
    });
  };

  const filterSprintTasks = async (button: {
    id: string;
    filters?: string[];
  }) => {
    setLoading(true);

    const current = sprints || [];

    if (!button.filters || button.filters.length === 0) {
      setFilteredSprints(current);
      setLoading(false);
      return;
    }

    const filtersLower = button.filters.map((f) => f.toLowerCase());

    const newData: PrjPhaseInterface[] = current.map((phase) => ({
      ...phase,
      phase_rules: Array.isArray(phase.phase_rules)
        ? phase.phase_rules.filter((rule) =>
            filtersLower.includes(rule.status.toLowerCase())
          )
        : [],
    }));

    setFilteredSprints(newData);
    setLoading(false);
  };

  const getContributors = () => {
    const users = [
      ...(Array.isArray(project?.employees) ? project?.employees : []),
      ...(Array.isArray(project?.users)
        ? project.users.map((user) => ({
            ...user,
            name: `${user.geb_voornaam || ''} ${user.geb_naam || ''}`.trim(),
          }))
        : []),
    ];
    setContributors(users);
  };

  const fetchAttachments = async () => {
    const files = await attachmentService.getProjectAttachments(
      companyId,
      projectId
    );
    setAttchments(files.attachments);
    setShowAttachments(files.attachments.length > 0);
  };

  const fetchSignedUrls = async () => {
    if (!attachments) {
      return;
    }
    setAttchmentsLoading(true);
    const signedUrls = await Promise.all(
      attachments.map(async (attachment: SqlAttachment) => {
        const response = await attachmentService.getSignedUrl(
          1,
          attachment.bij_bijlage
        );
        return {
          filename: attachment.bij_filename,
          url: response,
        };
      })
    );

    setUrls(signedUrls);
    setAttchmentsLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchProject();
    fetchAttachments();
    setLoading(false);
  }, [projectId, companyId]);

  useEffect(() => {
    filterSprintTasks('all');
    getContributors();
  }, [sprints]);

  const viewTaskDetail = (type: string, task: object) => {
    dispatch(
      setRootAside({
        component: 'projectTaskDetail',
        data: {
          task: task,
          projectId: projectId,
          type: type,
          companyId: companyId,
        },
        title: 'Taak',
      })
    );
  };

  const twoColors: ProgressProps['strokeColor'] = {
    '0%': '#50A2FF',
    '100%': '#1447E6',
  };

  // const fetchTickets = async (projectId: number) => {
  //   const tickets = await ticketService.getTickets(
  //     companyId,
  //     'PROJECT',
  //     projectId
  //   );
  //   return tickets;
  // };

  // const viewDetail = async (projectId: number) => {
  //   setTicketLoading(true);
  //   let ticket = await fetchTickets(projectId);

  //   if (ticket.zendesk.length === 0) {
  //     dispatch(
  //       addToast({
  //         style: 'error',
  //         description: t('Er is geen ticket gevonden voor dit project'),
  //       })
  //     );

  //     setTicketLoading(false);
  //     return;
  //   }

  //   dispatch(
  //     setRootAside({
  //       component: 'ticket',
  //       data: { ticket: ticket.zendesk[0] },
  //       title: 'Ticket',
  //     })
  //   );
  //   setTicketLoading(false);
  // };

  return (
    <>
      {loading && !project ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="mb-5 mt-5">
            <Link to="/project/overview">Projecten</Link>
            <span> &gt; </span>
            <Typography tag="span" type="semibold">
              Project
            </Typography>
          </div>
          {project ? (
            <div className="grid grid-cols-8 gap-8 p-1">
              <div
                className="col-span-3 sticky top-14 self-start 
                  flex flex-col justify-center mr-5"
              >
                <TextHeader
                  title={project.proj_onderwerp}
                  subtitle={project?.proj_number}
                  type="softHeader"
                  style="font-light text-xl"
                />
                <div className="grid grid-cols-2 my-5">
                  <div className="flex items-center gap-x-4">
                    {project.project_leader?.photo && (
                      <EmployeeThumb
                        image={project.project_leader.photo}
                        name={project.project_leader.name}
                        addClass="lg:w-10 lg:h-10 hover:scale-100 hover:outline outline-2 outline-offset-2"
                      />
                    )}
                    <div>
                      <Typography tag={'p'} type={'bold'}>
                        Projectleider
                      </Typography>
                      <Typography tag={'p'} type={'label'}>
                        {project.project_leader?.name ?? 'Niet toegewezen'}
                      </Typography>
                    </div>
                  </div>

                  <div>
                    {contributors ? (
                      <ProjectUsers users={contributors} />
                    ) : (
                      <LoadingSpinner />
                    )}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-3 gap-4 my-5">
                  <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4">
                    <Typography tag="span" type="label">
                      Sprints
                    </Typography>
                    <Typography tag="span" type="title">
                      <CountUp end={totals.phases} duration={1} />
                    </Typography>
                  </div>

                  <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4">
                    <Typography tag="span" type="label">
                      Taken
                    </Typography>
                    <Typography tag="span" type="title">
                      <CountUp end={totals.closedPhaseRules} duration={1} /> /{' '}
                      <CountUp end={totals.phaseRules} duration={1} />
                    </Typography>
                  </div>

                  <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4">
                    <Progress
                      percent={project.proj_progress}
                      type="circle"
                      size={80}
                      strokeColor={twoColors}
                      status="active"
                    />
                  </div>
                  {attachments && (
                    <div
                      className={`flex flex-col items-center justify-center rounded-lg border border-gray-200 p-4 cursor-pointer hover:border-blue-700 `}
                      onClick={fetchSignedUrls}
                    >
                      <Typography tag="span" type="label">
                        Bijlagen
                      </Typography>
                      <Typography tag="span" type="title">
                        {Object.keys(attachments).length}
                      </Typography>
                      {attachmentsLoading && <LoadingSpinner />}
                    </div>
                  )}
                </div>

                {project.proj_omschrijving && (
                  <div className="my-3">
                    <Typography tag="p" type="semibold" addClass={'pb-2'}>
                      Project scope
                    </Typography>
                    <Typography tag="p" type="label">
                      {project.proj_omschrijving}
                    </Typography>
                  </div>
                )}

                {showAttachments && (
                  <div className="grid grid-cols-3 gap-4">
                    <ListComponent
                      data={urls}
                      renderItem={(file: SignedUrls) => (
                        <DownloadableThumbnail url={file.url} />
                      )}
                    />
                  </div>
                )}
              </div>

              <div className="col-span-5 overflow-y-auto">
                <div className="mt-5">
                  <ListComponent
                    data={filteredSprints}
                    renderItem={(task) => (
                      <SprintItem
                        title={task.phase_subject}
                        percentage={task.percentageDone}
                        tasks={Object.keys(task.phase_rules).length}
                      >
                        <ListComponent
                          data={[...task.phase_rules].sort((a, b) =>
                            a.status.localeCompare(b.status)
                          )}
                          renderItem={(rule) => (
                            <div
                              onClick={() => viewTaskDetail(task.type, rule)}
                            >
                              <SprintListItem
                                title={rule.description}
                                startDate={rule.startDate}
                                endDate={rule.endDate}
                                status={rule.status}
                                important={rule.extra}
                                type={rule.type}
                                comments={rule.comments_count}
                              />
                            </div>
                          )}
                        />
                      </SprintItem>
                    )}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <LoadingSprints count={3} />
              <LoadingSprints count={2} />
            </>
          )}
        </>
      )}
    </>
  );
};

export default ProjectDetailContainer;
